<template>
  <div class="modal-backdrop" @click="emit('close')">
    <section
      class="converter-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="coordinate-converter-title"
      @click.stop
    >
      <div class="modal-header">
        <h3 id="coordinate-converter-title">Coordinate Converter</h3>
        <button class="close-button" type="button" aria-label="Close coordinate converter" @click="emit('close')">×</button>
      </div>

      <label for="coordinate-converter-input">Coordinate (any supported format)</label>
      <input
        id="coordinate-converter-input"
        v-model="coordinateText"
        type="text"
        placeholder="-32.9283, 151.7817"
        autocomplete="off"
        autofocus
      />

      <p v-if="conversion.error" class="error" role="alert">{{ conversion.error }}</p>

      <div v-if="conversion.formats.length" class="conversion-list" aria-live="polite">
        <div v-for="format in conversion.formats" :key="format.key" class="conversion-row">
          <span class="format-label">
            {{ format.label }}
            <span v-if="format.isDefault" class="default-badge">Default</span>
          </span>
          <output>{{ format.value }}</output>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { computed, ref } from "vue"
import { convertCoordinateFormats } from "../utils/coordinates"
import { normalizeCoordinateFormat } from "../utils/coordinateFormat"

const emit = defineEmits(["close"])
const props = defineProps({
  coordinateFormat: { type: String, default: "ddm" },
})
const coordinateText = ref("")

function prioritiseFormats(formats) {
  const preferred = normalizeCoordinateFormat(props.coordinateFormat)
  return [...formats]
    .sort((a, b) => Number(b.key === preferred) - Number(a.key === preferred))
    .map((format) => ({ ...format, isDefault: format.key === preferred }))
}

const conversion = computed(() => {
  if (!coordinateText.value.trim()) return { formats: [] }
  const result = convertCoordinateFormats(coordinateText.value)
  return { ...result, formats: prioritiseFormats(result.formats) }
})
</script>

<style scoped>
.modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1200;
  display: grid;
  place-items: center;
  padding: var(--space-4);
  background: var(--color-overlay);
}

.converter-modal {
  width: min(720px, 100%);
  max-height: min(760px, calc(100vh - var(--space-8)));
  overflow-y: auto;
  padding: var(--space-5);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  background: var(--color-bg-raised);
  color: var(--color-text);
  box-shadow: var(--shadow-lg);
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-4);
  margin-bottom: var(--space-4);
}

h3 {
  margin: 0;
}

.close-button {
  border: 0;
  background: transparent;
  color: var(--color-text-muted);
  font-size: 1.5rem;
  line-height: 1;
  cursor: pointer;
}

label {
  display: block;
  margin-bottom: var(--space-2);
  color: var(--color-text-muted);
  font-size: var(--fs-sm);
  font-weight: 600;
}

input {
  box-sizing: border-box;
  width: 100%;
  padding: var(--space-3);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-bg);
  color: var(--color-text);
  font: inherit;
}

input:focus {
  border-color: var(--color-accent);
  outline: 2px solid color-mix(in srgb, var(--color-accent) 25%, transparent);
  outline-offset: 1px;
}

.error {
  margin: var(--space-3) 0 0;
  color: var(--color-danger);
  font-size: var(--fs-sm);
}

.conversion-list {
  display: grid;
  gap: var(--space-2);
  margin-top: var(--space-4);
}

.conversion-row {
  display: grid;
  grid-template-columns: minmax(150px, 0.7fr) minmax(0, 1.8fr);
  gap: var(--space-3);
  align-items: baseline;
  padding: var(--space-3);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-bg-subtle);
}

.format-label {
  color: var(--color-text-muted);
  font-size: var(--fs-sm);
  font-weight: 600;
}

.default-badge {
  display: inline-block;
  margin-left: var(--space-2);
  padding: 1px var(--space-2);
  border-radius: 999px;
  background: var(--color-accent);
  color: white;
  font-size: var(--fs-xs);
}

output {
  overflow-wrap: anywhere;
  font-family: var(--font-mono);
  font-size: var(--fs-sm);
}

@media (max-width: 560px) {
  .conversion-row {
    grid-template-columns: 1fr;
    gap: var(--space-1);
  }
}
</style>
